import React from 'react';
import { DomainValuation } from '@/types';
import {
  formatValuationRange,
  getConfidenceColor,
  getConfidenceText,
  getValuationTier,
  getValuationSummary,
  scoreToPercentage,
  getFactorName,
  getFactorDescription,
  isValidValuation
} from '@/lib/valuation-utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Info, AlertCircle } from 'lucide-react';

interface DomainValuationProps {
  valuation?: DomainValuation;
  domain: string;
  className?: string;
}

const DomainValuationComponent: React.FC<DomainValuationProps> = ({
  valuation,
  domain: _domain,
  className = ''
}) => {
  if (!isValidValuation(valuation)) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-gray-400" />
            <span>Domain Valuation</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Valuation data not available for this domain</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { color, label } = getValuationTier(valuation!);
  const confidenceColor = getConfidenceColor(valuation!.confidence);
  const summary = getValuationSummary(valuation!);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <span>Domain Valuation</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Valuation Display */}
        <div className="text-center space-y-4">
          <div className="space-y-2">
            <div className={`text-3xl font-bold ${color}`}>
              {formatValuationRange(valuation!)}
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Badge
                variant="outline"
                className={`px-3 py-1 ${confidenceColor}`}
              >
                {label}
              </Badge>
              <Badge
                variant="outline"
                className={`px-3 py-1 ${confidenceColor}`}
              >
                {getConfidenceText(valuation!.confidence)}
              </Badge>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {summary}
          </p>
        </div>

        {/* Factor Breakdown */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <span>Valuation Factors</span>
            <div className="group relative">
              <Info className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Each factor contributes to the overall domain value
              </div>
            </div>
          </h4>

          <div className="space-y-3">
            {Object.entries(valuation!.factors).map(([factor, score]) => (
              <div key={factor} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {getFactorName(factor as keyof DomainValuation['factors'])}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {scoreToPercentage(score)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-md transition-all duration-500"
                    style={{ width: `${scoreToPercentage(score)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getFactorDescription(factor as keyof DomainValuation['factors'])}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900 dark:text-white">
            Analysis Details
          </h4>
          <div className="space-y-2">
            {valuation!.breakdown.map((item, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Valuation Disclaimer</p>
              <p>
                This is an automated estimate based on domain characteristics and market patterns.
                Actual domain values may vary significantly based on market conditions, buyer demand,
                and other factors not captured in this analysis.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DomainValuationComponent;
